import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauthenticated, object = signed in
  const [authReady, setAuthReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setAuthReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const _consume = (data) => {
    const pending = localStorage.getItem("pending_family_invite");
    if (pending) {
      localStorage.removeItem("pending_family_invite");
      api.post("/family/invites/accept", { token: pending })
        .then(({ data: res }) => toast.success(`You've joined ${res.family?.name || "the family"}`))
        .catch((e) => toast.error(e?.response?.data?.detail || "Family invite could not be accepted"));
    }
    if (data?.user) {
      setUser(data.user);
      return data.user;
    }
    setUser(data);
    return data;
  };

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      const u = _consume(data);
      return { ok: true, user: u };
    } catch (e) {
      const msg = e?.response?.data?.detail
        ? formatApiError(e.response.data.detail)
        : (e?.message || "Network error — could not reach the server.");
      return { ok: false, error: msg };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      const u = _consume(data);
      return { ok: true, user: u };
    } catch (e) {
      const msg = e?.response?.data?.detail
        ? formatApiError(e.response.data.detail)
        : (e?.message || "Network error — could not reach the server.");
      return { ok: false, error: msg };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, authReady, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
